import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { ApiConfig, ApiAuthConfig } from '@/api/types';
import { REDACTION_SENTINEL } from '@/api/types';
import { Field, FormShell, SecretInput } from './shared';
import { KeyValueEditor } from '../KeyValueEditor';
import { entriesToPairs, pairsToEntries } from '../key-value-pairs';

type AuthType = ApiAuthConfig['type'];

interface Props {
  initial: ApiConfig | null;
  onSubmit: (config: Partial<ApiConfig>) => void;
  submitting: boolean;
  onCancel: () => void;
  onReset?: () => void;
}

export function ApiConfigForm({ initial, onSubmit, submitting, onCancel, onReset }: Props): JSX.Element {
  const { t } = useTranslation();
  const [baseUrl, setBaseUrl] = React.useState(initial?.baseUrl ?? '');
  const [authType, setAuthType] = React.useState<AuthType>(initial?.auth?.type ?? 'none');
  const [username, setUsername] = React.useState(
    initial?.auth?.type === 'basic' ? (initial.auth.username ?? '') : '',
  );
  const [password, setPassword] = React.useState(
    initial?.auth?.type === 'basic' ? (initial.auth.password ?? '') : '',
  );
  const [token, setToken] = React.useState(
    initial?.auth?.type === 'bearer' ? (initial.auth.token ?? '') : '',
  );
  const [headerName, setHeaderName] = React.useState(
    initial?.auth?.type === 'api-key' ? (initial.auth.headerName ?? 'X-API-Key') : 'X-API-Key',
  );
  const [apiKeyValue, setApiKeyValue] = React.useState(
    initial?.auth?.type === 'api-key' ? (initial.auth.value ?? '') : '',
  );
  const [customHeaders, setCustomHeaders] = React.useState(() =>
    initial?.auth?.type === 'custom-headers' && initial.auth.headers
      ? entriesToPairs(initial.auth.headers)
      : [],
  );
  const [defaultHeaders, setDefaultHeaders] = React.useState(() =>
    initial?.defaultHeaders ? entriesToPairs(initial.defaultHeaders) : [],
  );
  const [timeout, setTimeout_] = React.useState(String(initial?.timeout ?? 30000));
  const [showHealthcheck, setShowHealthcheck] = React.useState(!!initial?.healthcheck);
  const [hcPath, setHcPath] = React.useState(initial?.healthcheck?.path ?? '/');
  const [hcMethod, setHcMethod] = React.useState<'GET' | 'HEAD'>(initial?.healthcheck?.method ?? 'GET');
  const [hcStatus, setHcStatus] = React.useState(String(initial?.healthcheck?.expectedStatus ?? 200));

  const buildAuth = (): ApiAuthConfig => {
    switch (authType) {
      case 'none':
        return { type: 'none' };
      case 'basic':
        return {
          type: 'basic',
          username: username.trim(),
          password: password === REDACTION_SENTINEL ? REDACTION_SENTINEL : password,
        };
      case 'bearer':
        return {
          type: 'bearer',
          token: token === REDACTION_SENTINEL ? REDACTION_SENTINEL : token,
        };
      case 'api-key':
        return {
          type: 'api-key',
          headerName: headerName.trim() || 'X-API-Key',
          value: apiKeyValue === REDACTION_SENTINEL ? REDACTION_SENTINEL : apiKeyValue,
        };
      case 'custom-headers':
        return { type: 'custom-headers', headers: pairsToEntries(customHeaders) };
    }
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const config: Partial<ApiConfig> = {
      baseUrl: baseUrl.trim(),
      auth: buildAuth(),
      timeout: Number(timeout) || 30000,
    };
    const dh = pairsToEntries(defaultHeaders);
    if (Object.keys(dh).length > 0) config.defaultHeaders = dh;
    if (showHealthcheck) {
      config.healthcheck = {
        path: hcPath.trim() || '/',
        method: hcMethod,
        expectedStatus: Number(hcStatus) || 200,
      };
    }
    onSubmit(config);
  };

  return (
    <form id="integration-form" onSubmit={handleSubmit}>
      <FormShell submitting={submitting} onCancel={onCancel} onReset={onReset}>
        <Field label={t('integrations.form.api.baseUrl')} htmlFor="api-url" required hint={t('integrations.form.api.baseUrlHint')}>
          <Input
            id="api-url"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder="https://api.example.com"
            required
          />
        </Field>

        <Field label={t('integrations.form.api.authType')} htmlFor="api-auth-type">
          <select
            id="api-auth-type"
            value={authType}
            onChange={(e) => setAuthType(e.target.value as AuthType)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="none">{t('integrations.form.api.authNone')}</option>
            <option value="basic">{t('integrations.form.api.authBasic')}</option>
            <option value="bearer">{t('integrations.form.api.authBearer')}</option>
            <option value="api-key">{t('integrations.form.api.authApiKey')}</option>
            <option value="custom-headers">{t('integrations.form.api.authCustom')}</option>
          </select>
        </Field>

        {authType === 'basic' && (
          <>
            <Field label={t('integrations.form.api.username')} htmlFor="api-user" required>
              <Input id="api-user" value={username} onChange={(e) => setUsername(e.target.value)} required />
            </Field>
            <Field label={t('integrations.form.api.password')} htmlFor="api-pwd" required>
              <SecretInput id="api-pwd" value={password} onChange={setPassword} />
            </Field>
          </>
        )}
        {authType === 'bearer' && (
          <Field label={t('integrations.form.api.token')} htmlFor="api-token" required>
            <SecretInput id="api-token" value={token} onChange={setToken} />
          </Field>
        )}
        {authType === 'api-key' && (
          <>
            <Field label={t('integrations.form.api.headerName')} htmlFor="api-hdr-name" hint={t('integrations.form.api.headerNameHint')}>
              <Input id="api-hdr-name" value={headerName} onChange={(e) => setHeaderName(e.target.value)} />
            </Field>
            <Field label={t('integrations.form.api.apiKeyValue')} htmlFor="api-key-val" required>
              <SecretInput id="api-key-val" value={apiKeyValue} onChange={setApiKeyValue} />
            </Field>
          </>
        )}
        {authType === 'custom-headers' && (
          <Field label={t('integrations.form.api.authHeaders')} htmlFor="api-custom-hdr">
            <KeyValueEditor
              value={customHeaders}
              onChange={setCustomHeaders}
              keyPlaceholder={t('integrations.form.api.headerKey')}
              valuePlaceholder={t('integrations.form.api.headerValue')}
              secretValues
            />
          </Field>
        )}

        <Field label={t('integrations.form.api.defaultHeaders')} htmlFor="api-default-hdr" hint={t('integrations.form.api.defaultHeadersHint')}>
          <KeyValueEditor
            value={defaultHeaders}
            onChange={setDefaultHeaders}
            keyPlaceholder={t('integrations.form.api.headerKey')}
            valuePlaceholder={t('integrations.form.api.headerValue')}
          />
        </Field>

        <Field label={t('integrations.form.api.timeout')} htmlFor="api-timeout" hint={t('integrations.form.api.timeoutHint')}>
          <Input
            id="api-timeout"
            type="number"
            value={timeout}
            onChange={(e) => setTimeout_(e.target.value)}
            min={1000}
            max={60000}
          />
        </Field>

        <button
          type="button"
          onClick={() => setShowHealthcheck((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          <ChevronDown className={`h-3 w-3 transition-transform ${showHealthcheck ? 'rotate-180' : ''}`} />
          {t('integrations.form.api.healthcheck')}
        </button>
        {showHealthcheck && (
          <div className="ml-4 space-y-3 border-l-2 border-muted pl-4">
            <Field label={t('integrations.form.api.hcPath')} htmlFor="hc-path" hint={t('integrations.form.api.hcPathHint')}>
              <Input id="hc-path" value={hcPath} onChange={(e) => setHcPath(e.target.value)} placeholder="/" />
            </Field>
            <Field label={t('integrations.form.api.hcMethod')} htmlFor="hc-method">
              <select
                id="hc-method"
                value={hcMethod}
                onChange={(e) => setHcMethod(e.target.value as 'GET' | 'HEAD')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="GET">GET</option>
                <option value="HEAD">HEAD</option>
              </select>
            </Field>
            <Field label={t('integrations.form.api.hcExpectedStatus')} htmlFor="hc-status" hint={t('integrations.form.api.hcExpectedStatusHint')}>
              <Input
                id="hc-status"
                type="number"
                value={hcStatus}
                onChange={(e) => setHcStatus(e.target.value)}
              />
            </Field>
          </div>
        )}
      </FormShell>
    </form>
  );
}
